import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useEditProduct } from "../../../lib/query/mutations/useProductMutations";
import { ArrowLeft, ShieldExclamation } from "react-bootstrap-icons";
import { useMerchantAuth, useMultiAuthCheck } from "../../../utilities/authCheck";
import { useUserContext } from "../../../context/UserContext";
import { toast } from "../../../utilities/toast";
import { useFormDraft } from "../../../hooks/useFormDraft";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

if (typeof document !== "undefined" && !document.getElementById("ep-styles")) {
  const s = document.createElement("style");
  s.id = "ep-styles";
  s.textContent = `
    @keyframes _ep_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._ep_sk { animation: _ep_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .ep-input { background: rgba(118,118,128,0.10) !important; border: 1px solid transparent !important; border-radius: 10px !important; padding: 11px 14px !important; font-size: 0.9375rem !important; color: #1c1c1e !important; transition: border-color .18s, box-shadow .18s !important; width: 100%; outline: none; font-family: inherit; box-sizing: border-box; }
    .ep-input:focus { border-color: #007AFF !important; box-shadow: 0 0 0 3px rgba(0,122,255,0.12) !important; background: rgba(118,118,128,0.08) !important; }
    .ep-input::placeholder { color: rgba(60,60,67,0.32) !important; }
  `;
  document.head.appendChild(s);
}

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_ep_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 7 }}>
    {children}
  </label>
);

const EditProduct = () => {
  const { storeId, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthorized, isLoading: authLoading, logout } = useMultiAuthCheck(["elite", "merchant"]);
  const { token } = useUserContext();

  const [name,        setName]        = useState("");
  const [price,       setPrice]       = useState(0);
  const [imageFile,   setImageFile]   = useState("");
  const [description, setDescription] = useState("");
  const [category,    setCategory]    = useState("");
  const [type,        setType]        = useState("");
  const [subType,     setSubType]     = useState("");
  const [validated,   setValidated]   = useState(false);
  const { mutateAsync: editProduct, isPending: btnLoading } = useEditProduct();

  const { restoredDraft, clearDraft } = useFormDraft(`edit-product:${productId}`, {
    name, price, imageFile, description, category, type, subType,
  });

  useEffect(() => {
    const source = restoredDraft ?? {
      name: location.state?.name,
      price: location.state?.price,
      imageFile: location.state?.imageSrc,
      description: location.state?.description,
      category: location.state?.category,
      type: location.state?.type,
      subType: location.state?.subType,
    };
    setName(source.name ?? "");
    setPrice(source.price ?? 0);
    setImageFile(source.imageFile ?? "");
    setDescription(source.description ?? "");
    setCategory(source.category ?? "");
    setType(source.type ?? "");
    setSubType(source.subType ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNameChange        = (e) => setName(e.target.value);
  const handlePriceChange       = (e) => setPrice(Number(e.target.value));
  const handleFileChange        = (e) => setImageFile(e.target.value);
  const handleDescriptionChange = (e) => setDescription(e.target.value);
  const handleCategoryChange    = (e) => setCategory(e.target.value);
  const handleTypeChange        = (e) => setType(e.target.value);
  const handleSubTypeChange     = (e) => setSubType(e.target.value);

  const rollback = () => navigate(-1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);
    const formData = {
      id: location.state?.id ?? productId, name, price,
      imageSrc: imageFile, description, category, storeId, type, subType,
    };
    try {
      await editProduct({ product: formData, token });
      toast.success("Product updated successfully!");
      clearDraft();
      rollback();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update product. Try again later.");
    }
  };

  if (authLoading) {
    return (
      <div style={PAGE}>
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} /><Skel w={130} h={22} r={7} />
          </div>
          <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.08)", padding: 20 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <Skel w={60} h={11} r={5} style={{ marginBottom: 8 }} />
                <Skel w="100%" h={42} r={10} />
              </div>
            ))}
            <Skel w="100%" h={44} r={12} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: 32 }}>
          <div style={{ width: 60, height: 60, borderRadius: 18, backgroundColor: "rgba(255,149,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>Access Restricted</h3>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,.5)", textAlign: "center", marginBottom: 24 }}>Merchant authentication required.</p>
          <button onClick={logout} style={{ padding: "11px 28px", borderRadius: 12, border: "none", backgroundColor: "#007AFF", color: "#fff", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
            Sign In as Merchant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Edit Product")} />
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Back nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1c1c1e", flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>Edit Product</h2>
        </div>

        <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.09)", padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
          <form noValidate onSubmit={handleSubmit}>

            {/* Name + Price */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <FieldLabel htmlFor="ep-name">Name</FieldLabel>
                <input
                  id="ep-name"
                  type="text"
                  className="ep-input"
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Enter name"
                  required
                />
                {validated && !name && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a name</div>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="ep-price">Price</FieldLabel>
                <input
                  id="ep-price"
                  type="number"
                  className="ep-input"
                  value={price}
                  onChange={handlePriceChange}
                  placeholder="KSH"
                  required
                />
                {validated && !price && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Required</div>
                )}
              </div>
            </div>

            {/* Image link */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel htmlFor="ep-image">Image link</FieldLabel>
              <input
                id="ep-image"
                type="text"
                className="ep-input"
                value={imageFile}
                placeholder="Src"
                onChange={handleFileChange}
                required
              />
              {validated && !imageFile && (
                <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please select an image file.</div>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel htmlFor="ep-description">Description</FieldLabel>
              <textarea
                id="ep-description"
                className="ep-input"
                value={description}
                placeholder="Write a description..."
                onChange={handleDescriptionChange}
                rows={6}
                required
                style={{ resize: "vertical" }}
              />
              {validated && !description && (
                <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a description.</div>
              )}
            </div>

            {/* Category / Type / Sub type */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
              <div>
                <FieldLabel htmlFor="ep-category">Category</FieldLabel>
                <input
                  id="ep-category"
                  type="text"
                  className="ep-input"
                  value={category}
                  onChange={handleCategoryChange}
                  placeholder="Category"
                  required
                />
                {validated && !category && (
                  <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a category.</div>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="ep-type">Type</FieldLabel>
                <input
                  id="ep-type"
                  type="text"
                  className="ep-input"
                  value={type}
                  onChange={handleTypeChange}
                  placeholder="Type"
                />
              </div>
              <div>
                <FieldLabel htmlFor="ep-subtype">Sub type</FieldLabel>
                <input
                  id="ep-subtype"
                  type="text"
                  className="ep-input"
                  value={subType}
                  onChange={handleSubTypeChange}
                  placeholder="Sub type"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={btnLoading}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, border: "none",
                backgroundColor: btnLoading ? "rgba(0,122,255,.5)" : "#007AFF",
                color: "#fff", fontSize: "0.9375rem", fontWeight: 600,
                cursor: btnLoading ? "not-allowed" : "pointer",
                fontFamily: FONT, transition: "background-color .18s",
              }}
            >
              {btnLoading ? "Saving…" : "Save Changes"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
